import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import DeliveryAddressCard from '../common/DeliveryAddressCard';
import PaymentLinkCard from '../common/PaymentLinkCard';
import { useBookingPaymentLink } from '../../hooks/useBookingPaymentLink';
import { currencyRounded, formatDateTime, getStatusBadgeStyle, maskMobile } from '../../utils/subscriberHelpers';
import { formatSlotDateTime } from '../../utils/dateFormatter';

const DiagnosticBookingDetailModal = ({
  mounted,
  bookingDetail,
  loading,
  error,
  onRetry,
  onClose,
  showFullMobile = false,
  partnerDataExpanded,
  onTogglePartnerData,
  getStatusStyle = getStatusBadgeStyle,
  // Ops / SuperAdmin only: (re)send the Razorpay link for an unpaid ops-built order.
  currentRole,
  canSendPaymentLink = false,
}) => {
  const { generatingLink, paymentLink, generateLink } = useBookingPaymentLink(currentRole);
  // The modal stays mounted across bookings, so remember which order the shown link is for.
  const [linkOrderId, setLinkOrderId] = useState(null);
  const [linkNotice, setLinkNotice] = useState(null);

  if (!mounted) return null;

  // The modal stays mounted, so a notice must not greet the next opening of the same order.
  const handleClose = () => {
    setLinkNotice(null);
    onClose();
  };

  // contract: internalapi/views/user_type/shared/diagnostic_subscribers/snapshot.py (manual_order_id, status)
  const manualOrderId = bookingDetail?.manual_order_id || null;
  const showPaymentLink = canSendPaymentLink && Boolean(manualOrderId)
    && String(bookingDetail?.status || '').toLowerCase().trim() === 'initiated';
  const shownLink = showPaymentLink && linkOrderId === manualOrderId ? paymentLink : null;

  // The backend re-sends an unexpired link for the same amount, else creates a new one.
  const handleSendPaymentLink = async () => {
    setLinkNotice(null);
    const link = await generateLink(manualOrderId, null, (message, type) => setLinkNotice({ message, type, orderId: manualOrderId }));
    if (link) setLinkOrderId(manualOrderId);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top stripe */}
        <div className="h-1.5 bg-gradient-to-r from-orange-400 via-orange-500 to-amber-400 shrink-0" />

        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-br from-orange-50 to-amber-50/40 border-b border-orange-100 shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center shrink-0 shadow-sm shadow-orange-200">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <p className="text-base font-bold text-gray-900 leading-tight">Booking Details</p>
                  {bookingDetail?.status && (
                    <span
                      className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full border"
                      style={getStatusStyle(bookingDetail.status)}
                    >
                      {bookingDetail.status}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  {bookingDetail?.booking_id && (
                    <span className="text-xs text-gray-400 font-mono">#{bookingDetail.booking_id}</span>
                  )}
                  {bookingDetail?.date_time && (
                    <span className="text-xs text-gray-400">{formatDateTime(bookingDetail.date_time)}</span>
                  )}
                  {bookingDetail?.service_provider && (
                    <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-100">
                      {bookingDetail.service_provider}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white/80 transition-colors shrink-0"
            >
              <FaTimes size={14} />
            </button>
          </div>

          {/* Financial stat pills */}
          {bookingDetail && (() => {
            const testsTotal = (bookingDetail.member_tests || []).reduce((s, m) => s + (m.products || []).reduce((ps, p) => ps + (Number(p.mrp) || 0), 0), 0);
            const chargesTotal = bookingDetail.charges ? [bookingDetail.charges.delivery_charges, bookingDetail.charges.handling_packaging_charge, bookingDetail.charges.platform_charge, bookingDetail.charges.partner_charges].reduce((s, v) => s + (Number(v) || 0), 0) : 0;
            const totalPrice = bookingDetail.price != null && Number.isFinite(Number(bookingDetail.price))
              ? Number(bookingDetail.price)
              : testsTotal + chargesTotal;
            return (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-4">
                {[
                  { label: 'Total Price', value: `₹${totalPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, highlight: true },
                  { label: 'Cashback', value: currencyRounded(bookingDetail.cashback || 0) },
                  { label: 'Eligible CB', value: currencyRounded(bookingDetail.eligible_cashback || 0) },
                  { label: 'Redeemable CB', value: currencyRounded(bookingDetail.redeemable_cashback || 0) },
                  { label: 'Wallet Bal.', value: currencyRounded(bookingDetail.wallet_balance || 0) },
                ].map(({ label, value, highlight }) => (
                  <div key={label} className={`rounded-xl px-3 py-2.5 text-center ${highlight ? 'bg-orange-500 shadow-sm shadow-orange-200' : 'bg-white border border-gray-100'}`}>
                    <p className={`text-[10px] font-medium uppercase tracking-wide mb-0.5 ${highlight ? 'text-orange-100' : 'text-gray-400'}`}>{label}</p>
                    <p className={`text-sm font-bold tabular-nums ${highlight ? 'text-white' : 'text-gray-800'}`}>{value}</p>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-8 h-8 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-400">Loading booking details…</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <p className="text-sm text-red-500">{error}</p>
              <button
                onClick={onRetry}
                className="px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : bookingDetail ? (
            <div className="p-5 space-y-4">

              {/* Info grid */}
              <div className={`grid grid-cols-1 gap-4 ${bookingDetail?.charges ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>

                {/* Booking Info */}
                <div className="rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Booking Info</p>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {[
                      { label: 'Subscriber', value: bookingDetail.subscriber_name || 'N/A' },
                      { label: 'Mobile', value: !showFullMobile ? maskMobile(bookingDetail.mobile_number) : (bookingDetail.mobile_number || 'N/A'), mono: true },
                      { label: 'Appointment', value: formatSlotDateTime(bookingDetail.appointment_date, bookingDetail.appointment_time) || 'N/A', wrap: true },
                      { label: 'Family Members', value: bookingDetail.family_member_names || 'N/A' },
                      { label: 'Payment Method', value: (bookingDetail.status || '').toLowerCase() === 'initiated' ? 'N/A' : (bookingDetail.payment_method || 'N/A') },
                    ].map(({ label, value, mono, wrap }) => (
                      <div key={label} className="flex items-center justify-between px-4 py-2.5 gap-3">
                        <span className={`text-xs text-gray-400 shrink-0 ${wrap ? '' : 'w-28'}`}>{label}</span>
                        <span
                          title={typeof value === 'string' ? value : undefined}
                          className={`text-xs font-semibold text-gray-800 text-right ${wrap ? 'min-w-0 break-words' : 'truncate'} ${mono ? 'font-mono' : ''}`}
                        >
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* References */}
                <div className="rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">References</p>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {[
                      { label: 'Ref Code', value: bookingDetail.ref_code || '—' },
                      { label: 'AP Txn ID', value: bookingDetail.ap_transaction_id || '—' },
                      { label: '3P Booking Ref', value: bookingDetail.third_party_booking_ref_code || '—' },
                      { label: 'Booking ID', value: bookingDetail.booking_id || '—' },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-center justify-between px-4 py-2.5 gap-4">
                        <span className="text-xs text-gray-400 shrink-0 w-28">{label}</span>
                        <span className="text-xs font-semibold text-gray-800 font-mono text-right truncate">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Charges */}
                {bookingDetail?.charges && (
                  <div className="rounded-xl border border-gray-100 overflow-hidden">
                    <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                      <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Charges</p>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {[
                        { label: 'Delivery', value: bookingDetail.charges.delivery_charges },
                        { label: 'Handling & Packaging', value: bookingDetail.charges.handling_packaging_charge },
                        { label: 'Platform', value: bookingDetail.charges.platform_charge },
                        { label: 'Partner', value: bookingDetail.charges.partner_charges },
                      ].filter(({ value }) => value != null).map(({ label, value }) => {
                        const isZero = Number(value) === 0;
                        return (
                          <div key={label} className="flex items-center justify-between px-4 py-2.5">
                            <span className="text-xs text-gray-400">{label}</span>
                            <span className={`text-xs font-semibold tabular-nums ${isZero ? 'text-gray-300' : 'text-gray-800'}`}>
                              ₹{Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50">
                        <span className="text-xs font-bold text-gray-600">Total</span>
                        <span className="text-sm font-bold text-gray-900 tabular-nums">
                          ₹{[bookingDetail.charges.delivery_charges, bookingDetail.charges.handling_packaging_charge, bookingDetail.charges.platform_charge, bookingDetail.charges.partner_charges].reduce((s, v) => s + (Number(v) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Collection Address — same component the medicine snapshot uses */}
              <DeliveryAddressCard
                address={bookingDetail?.delivery_address}
                title="Collection Address"
                className="rounded-xl border border-gray-100 p-4"
              />

              {/* Tests Booked per Member */}
              {bookingDetail?.member_tests?.length > 0 && (() => {
                const totalTests = bookingDetail.member_tests.reduce((n, m) => n + (m.products || []).length, 0);
                const grandTotal = bookingDetail.member_tests.reduce((s, m) => s + (m.products || []).reduce((ps, p) => ps + (Number(p.mrp) || 0), 0), 0);
                return (
                  <div className="rounded-xl border border-orange-100 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-orange-50 to-amber-50/40 border-b border-orange-100">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                        <p className="text-[11px] font-bold text-orange-600 uppercase tracking-widest">Tests Booked</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {grandTotal > 0 && (
                          <span className="text-xs font-bold text-gray-700 tabular-nums">₹{grandTotal.toFixed(2)}</span>
                        )}
                        <span className="text-xs font-semibold text-orange-600 bg-white border border-orange-200 px-2.5 py-0.5 rounded-full">
                          {totalTests} test{totalTests !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {bookingDetail.member_tests.map((memberEntry, idx) => {
                        const products = memberEntry.products || [];
                        const memberTotal = products.reduce((s, p) => s + (Number(p.mrp) || 0), 0);
                        const initials = (memberEntry.name || memberEntry.member_name || 'M').charAt(0).toUpperCase();
                        return (
                          <div key={idx}>
                            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50/70">
                              <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-amber-400 flex items-center justify-center text-white text-[11px] font-bold shrink-0 shadow-sm">
                                  {initials}
                                </div>
                                <div>
                                  <span className="text-xs font-semibold text-gray-800">
                                    {memberEntry.name || memberEntry.member_name || `Member ${idx + 1}`}
                                  </span>
                                  <span className="text-[11px] text-gray-400 ml-1.5">{products.length} test{products.length !== 1 ? 's' : ''}</span>
                                </div>
                              </div>
                              {memberTotal > 0 && (
                                <span className="text-xs font-bold text-gray-700 tabular-nums bg-white border border-gray-100 px-2 py-0.5 rounded-lg">₹{memberTotal.toFixed(2)}</span>
                              )}
                            </div>
                            {products.length > 0 && (
                              <div className="divide-y divide-gray-50">
                                {products.map((product, pIdx) => (
                                  <div key={pIdx} className="flex items-center justify-between px-4 pl-14 py-2 hover:bg-gray-50/50 transition-colors">
                                    <span className="text-xs text-gray-600">{product.test_name || product.name || 'N/A'}</span>
                                    {product.mrp != null && (
                                      <span className="text-xs font-semibold text-gray-800 tabular-nums ml-4 shrink-0">₹{product.mrp}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Partner Data */}
              {bookingDetail && (
                <div className="rounded-xl border border-gray-100 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                    onClick={onTogglePartnerData}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                      <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Partner Data</p>
                    </div>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${partnerDataExpanded ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {partnerDataExpanded && (
                    <div className="overflow-auto max-h-72 border-t border-gray-100 bg-gray-50/50">
                      {bookingDetail.partner_data !== undefined && bookingDetail.partner_data !== null ? (
                        <pre className="px-4 py-3 text-xs text-gray-700 font-mono leading-relaxed whitespace-pre">
                          {JSON.stringify(bookingDetail.partner_data, null, 2)}
                        </pre>
                      ) : (
                        <p className="px-4 py-4 text-xs text-gray-400 text-center">No partner data available</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {showPaymentLink && shownLink && (
                <PaymentLinkCard link={shownLink} onResend={handleSendPaymentLink} resending={generatingLink} />
              )}

            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 shrink-0">
          {showPaymentLink && linkNotice?.orderId === manualOrderId && (
            <p className={`mr-auto text-xs ${linkNotice.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
              {linkNotice.message}
            </p>
          )}
          {showPaymentLink && !shownLink && (
            <button
              onClick={handleSendPaymentLink}
              disabled={generatingLink}
              title="Sends a Razorpay link for the amount after cashback"
              className={`px-5 py-2 rounded-xl text-sm font-semibold border transition-colors ${generatingLink
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : 'border-orange-500 text-orange-600 bg-white hover:bg-orange-50'
                }`}
            >
              {generatingLink ? 'Sending…' : 'Resend Payment Link'}
            </button>
          )}
          <button
            onClick={handleClose}
            className="px-5 py-2 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DiagnosticBookingDetailModal;
